import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const chapters = [
  {
    num: "01",
    title: "Money as a language",
    body:
      "Every payment is a sentence — between people, machines and institutions. My job is to make the grammar effortless: fast, safe, and quietly beautiful.",
  },
  {
    num: "02",
    title: "Ship small, ship real",
    body:
      "Beta over vaporware. I ship the smallest thing that changes behavior, measure the truth in the data, and let the roadmap earn its next chapter.",
  },
  {
    num: "03",
    title: "AI without vertigo",
    body:
      "AI in payments doesn't need spectacle — it needs evaluation, guardrails and trust rails. I build the boring parts so the magic feels safe.",
  },
  {
    num: "04",
    title: "Beauty is a feature",
    body:
      "The best money products disappear into the moment. Design isn't decoration; it's the difference between a user and an advocate.",
  },
];

const ChapterRow = ({ c, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1.0, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-12 gap-6 py-10 md:py-14 border-t border-[var(--bone)]/10 group"
      data-testid={`manifesto-chapter-${c.num}`}
    >
      <div className="col-span-2 md:col-span-1 chapter-num text-sm">
        {c.num}
      </div>
      <div className="col-span-10 md:col-span-5">
        <h3 className="font-serif text-3xl md:text-5xl leading-[0.98] text-[var(--bone)] group-hover:italic transition-all duration-500">
          {c.title}.
        </h3>
      </div>
      <div className="col-span-12 md:col-span-5 md:col-start-8 text-[var(--bone-dim)] text-base md:text-lg leading-relaxed font-light max-w-md">
        {c.body}
      </div>
      <div className="col-span-1 hidden md:flex justify-end items-start text-[var(--copper)] text-xl opacity-0 group-hover:opacity-100 transition-opacity">
        ✦
      </div>
    </motion.div>
  );
};

const Manifesto = () => {
  return (
    <section
      id="manifesto"
      className="relative bg-[var(--ink)] py-28 md:py-40"
      data-testid="manifesto-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20%" }}
          transition={{ duration: 0.9 }}
          className="mb-16 md:mb-20 flex items-end justify-between flex-wrap gap-6"
        >
          <div>
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[var(--copper)] mb-4">
              [ Chapter II — Manifesto ]
            </div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-[var(--bone)] max-w-3xl">
              Four things I believe
              <br />
              <span className="italic text-[var(--bone-dim)]">about building money.</span>
            </h2>
          </div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--bone-dim)] max-w-[220px]">
            A working philosophy, revised monthly, argued daily.
          </div>
        </motion.div>

        <div>
          {chapters.map((c, i) => (
            <ChapterRow c={c} i={i} key={c.num} />
          ))}
          <div className="border-t border-[var(--bone)]/10" />
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
